<template>
  <div class="dg" id="rally-image-modal-container">
    <div class="rally-image-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Select Rally Image</h2>
          <i class="fal fa-times exit" @click="handleCancel"></i>
        </div>

        <div class="photo-card">
          <div class="card-header">
            <div>
              <h3>Add a photo</h3>

              <p>
                Upload an image for your Rally's cover photo. Your Rally image
                will also appear in the search results in your dashboard.
              </p>
            </div>

            <div class="popover">
              <Popover direction="bottom" :auto-close-delay="5000">
                <div class="photo-guidelines-popover">
                  <p class="guidelines-title">
                    Guidelines - File type must be a JPEG or PNG.
                  </p>
                  <ul class="guidelines-list">
                    <li>The recommended image size is at least 306px by 920px</li>
                    <li>
                      You must own the images you upload or have permission from
                      the copyright holder to use them.
                    </li>
                  </ul>
                </div>
              </Popover>
            </div>
          </div>

          <div v-if="!image" class="empty-upload">
            <input
              ref="fileInput"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/svg+xml"
              hidden
              @change="handleFileChange"
            />

            <button type="button" class="add-image-button" @click="openFilePicker">
              <img src="/photo-icon.svg" alt="photo icon" />
              Add Cover Image
            </button>
          </div>


          <div v-else class="cropper-wrapper">
            <Cropper
              ref="cropper"
              class="cropper"
              :src="image"
              :stencil-props="{ aspectRatio: aspectRatio }"
              :resize-image="{
                adjustStencil: false,
                wheel: false,
                touch: false
              }"
              :image-restriction="'stencil'"
              :transitions="false"
              @ready="onCropperReady"
            />
          </div>

          <div v-if="image" class="controls-wrapper">
            <div class="rotate-controls">
              <div class="rotate-actions" @click="rotateLeft">
                <img src="/arrow-rotate-left.svg" alt="rotate left" />
              </div>

              <div class="rotate-actions" @click="rotateRight">
                <img src="/arrow-rotate-right.svg" alt="rotate right" />
              </div>
            </div>


            <div class="crop-controls">
              <div class="round-control" @click="zoomOut">
                <img src="/minus-rounded.svg" alt="zoom out" />
              </div>

              <div class="zoom-slider-wrapper">
                <input
                  :value="zoomValue"
                  class="zoom-slider"
                  type="range"
                  :min="minZoom"
                  :max="maxZoom"
                  step="0.01"
                  @input="handleZoom"
                />
              </div>

              <div
                type="button"
                class="round-control"
                aria-label="Zoom in"
                @click="zoomIn"
              >
                <img src="/plus-rounded.svg" alt="zoom in" />
              </div>
            </div>
          </div>


          <div v-if="image" class="photo-actions">
            <button type="button" class="delete-button" @click="deletePhoto">
              <img src="/trash-icon.svg" alt="delete" />
              Delete Photo
            </button>

            <button type="button" class="change-button" @click="openFilePicker">
              Change Photo
            </button>

            <input
              ref="fileInputChange"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/svg+xml"
              hidden
              @change="handleFileChange"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="handleCancel">
            Cancel
          </button>

          <button
            type="button"
            class="upload-btn"
            :disabled="!image || uploading"
            @click="uploadImage"
          >
            {{ uploading ? 'Uploading...' : 'Upload Image' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { eventBus } from '@/main.js';
import { userInfo } from '@/common/user-info';
import Popover from '@/components/shared/Popover.vue';

export default {
  name: 'SelectPhotoModal',
  components: {
    Cropper,
    Popover
  },
  props: {
    aspectRatio: {
      type: Number,
      default: 9 / 3
    },
    modelValue: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    initialImage: {
      type: String,
      default: ''
    },
    minZoom: {
      type: Number,
      default: 1
    },
    maxZoom: {
      type: Number,
      default: 3
    },
    saveImage: {
      type: Function,
      default: null
    },
    deleteImage: {
      type: Function,
      default: null
    },
    rallyId: {
      type: [Number, String],
      default: null
    },
    contextType: {
      type: String,
      default: 'Rally'
    },
    contextId: {
      type: [Number, String],
      default: null
    },
    avatarType: {
      type: String,
      default: 'RallyAvatar'
    },
    uniqueId: {
      type: String,
      default: ''
    },
    directUpload: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      image: this.initialImage || this.modelValue || this.value || '',
      originalFile: null,
      imgData: null,
      imgParams: {},
      tempKey: "TK" + Math.floor(Math.random() * 10000),
      zoomValue: this.minZoom,
      initialCropWidth: null,
      uploading: false,
      objectUrl: null
    };
  },
  computed: {
    ...mapState('contents', ['uploadCount']),
    effectiveRallyId() {
      return this.rallyId || this.$route?.params?.rallyId || null;
    },
    currentUserId() {
      try {
        const info = userInfo();
        return info ? info.user_id : null;
      } catch (e) {
        return null;
      }
    }
  },
  watch: {
    initialImage(val) {
      if (val !== undefined) {
        this.image = val || '';
      }
    },
    modelValue(val) {
      if (val !== undefined) {
        this.image = val || '';
      }
    },
    value(val) {
      if (val !== undefined) {
        this.image = val || '';
      }
    }
  },
  beforeDestroy() {
    this.cleanupObjectUrl();
  },
  beforeUnmount() {
    this.cleanupObjectUrl();
  },
  methods: {
    ...mapActions('contents', ['fetchContents', 'uploadFile']),
    ...mapActions('user', ['setModalProps']),

    cleanupObjectUrl() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }
    },

    readFileAsDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = e => reject(e);
        reader.readAsDataURL(file);
      });
    },

    loadImage(dataUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = err => reject(err);
        img.src = dataUrl;
      });
    },

    downscale(canvas, maxW, maxH) {
      const scale = Math.min(maxW / canvas.width, maxH / canvas.height, 1);
      if (scale === 1) return canvas;
      const c = document.createElement('canvas');
      c.width = Math.round(canvas.width * scale);
      c.height = Math.round(canvas.height * scale);
      c.getContext('2d').drawImage(canvas, 0, 0, c.width, c.height);
      return c;
    },

    cacheAvatar(img) {
      this.imgData = img;
      this.image = img.dataUrl;
      this.zoomValue = this.minZoom;
      this.initialCropWidth = null;
    },


    close() {
      this.setModalProps({});
    },

    openFilePicker() {
      const input = this.$refs.fileInput || this.$refs.fileInputChange;
      if (input) {
        if (Array.isArray(input)) {
          input[0]?.click();
        } else {
          input.click();
        }
      }
    },

    async handleFileChange(event) {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'].includes(file.type)) {
        window.alert('Please select a JPG or PNG image.');
        return;
      }

      this.originalFile = file;
      this.cleanupObjectUrl();

      try {
        const dataUrl = await this.readFileAsDataURL(file);
        const image = await this.loadImage(dataUrl);

        this.cacheAvatar({
          dataUrl,
          name: file.name,
          type: file.type,
          width: image.width,
          height: image.height,
          originalWidth: image.width,
          originalHeight: image.height,
          file
        });
      } catch (err) {
        console.error('Error loading image:', err);
      }

      event.target.value = '';
    },

    onCropperReady() {
      const coordinates = this.$refs.cropper?.getResult()?.coordinates;

      if (coordinates?.width) {
        this.initialCropWidth = coordinates.width;
      }

      this.zoomValue = this.minZoom;
    },


    rotateLeft() {
      this.$refs.cropper?.rotate(-90);
    },


    rotateRight() {
      this.$refs.cropper?.rotate(90);
    },


    handleZoom(event) {
      if (event?.target) {
        this.zoomValue = Number(event.target.value);
      }

      if (!this.$refs.cropper || !this.initialCropWidth) {
        return;
      }

      const newZoom = this.zoomValue;
      const coordinates = this.$refs.cropper.getResult()?.coordinates;
      const currentCropWidth = coordinates?.width;

      if (!currentCropWidth) {
        return;
      }

      const currentZoom = this.initialCropWidth / currentCropWidth;
      const factor = newZoom / currentZoom;

      if (factor !== 1) {
        this.$refs.cropper.zoom(factor);
      }
    },

    zoomOut() {
      const value = Math.max(this.minZoom, this.zoomValue - 0.1);
      this.zoomValue = Number(value.toFixed(2));
      this.handleZoom();
    },

    zoomIn() {
      const value = Math.min(this.maxZoom, this.zoomValue + 0.1);
      this.zoomValue = Number(value.toFixed(2));
      this.handleZoom();
    },

    deletePhoto() {
      this.cleanupObjectUrl();

      this.image = '';
      this.originalFile = null;
      this.imgData = null;
      this.imgParams = {};
      this.zoomValue = this.minZoom;
      this.initialCropWidth = null;

      if (typeof this.deleteImage === 'function') {
        this.deleteImage();
      }
    },

    handleCancel() {
      this.close();
    },

    canvasToBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          blob => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas could not be converted to Blob.'));
            }
          },
          'image/jpeg',
          0.92
        );
      });
    },

    async uploadImage() {
      if (!this.$refs.cropper || !this.image || this.uploading) {
        return;
      }

      this.uploading = true;

      try {
        const result = this.$refs.cropper.getResult();
        if (!result?.canvas) {
          throw new Error('Unable to generate cropped image.');
        }

        const c = this.downscale(result.canvas, 1800, 600);
        const dataUrl = c.toDataURL();
        const mimeMatch = dataUrl.match(/^data:([^;]+);/);
        const canvasMime = mimeMatch ? mimeMatch[1] : null;

        const uploadedMime = this.imgData && this.imgData.type ? this.imgData.type : null;
        const attachmentContentType = canvasMime || uploadedMime || 'image/png';

        const uploadedName = this.imgData && this.imgData.name
          ? this.imgData.name
          : (this.originalFile && this.originalFile.name ? this.originalFile.name : null);
        const ext = attachmentContentType.split('/')[1] || 'png';
        const fallbackName = `avatar_${Date.now()}.${ext}`;
        const attachmentFileName = uploadedName || fallbackName;

        const contextRallyId = this.effectiveRallyId;
        const contextId = this.contextId || this.effectiveRallyId || this.currentUserId;
        const contextType = this.contextType || 'Rally';
        const type = this.avatarType || (contextType === 'User' ? 'UserAvatar' : 'RallyAvatar');

        this.imgParams = {
          contentType: 'Binary',
          contextType,
          type,
          contextId,
          contextRallyId,
          creatorId: this.currentUserId,
          status: 1,
          attachment: dataUrl.split(',')[1],
          attachmentDataURL: dataUrl,
          attachmentContentType,
          attachmentFileName,
          tempKey: this.tempKey,
          file: this.imgData?.file || this.originalFile || null
        };

        const blob = await this.canvasToBlob(c);
        const croppedFile = new File(
          [blob],
          attachmentFileName,
          {
            type: attachmentContentType
          }
        );

        if (typeof this.saveImage === 'function') {
          this.saveImage(dataUrl, this.imgParams, croppedFile);
          this.close();
        }

        if (typeof this.saveImage !== 'function' || this.directUpload) {
          eventBus.emit('overlayOn');
          await this.uploadFile(this.imgParams);
          if (this.$store?.dispatch) {
            this.$store.dispatch('user/flashSuccess', 'Image uploaded successfully!');
          }
          this.close();
        }
      } catch (error) {
        console.error('Unable to crop/upload image:', error);
      } finally {
        this.uploading = false;
      }
    }
  }
};
</script>
